import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { 
  CreditCard, Check, Zap, AlertCircle, ShieldCheck, 
  Star, Calendar, RefreshCw, Tag, X, ChevronRight, 
  Activity, Shield, Globe, Cpu, ArrowUpRight, TrendingUp,
  Award, Package, Clock, Info, CheckCircle, Database, Smartphone
} from "lucide-react";
import DashboardModuleShell from "../components/DashboardModuleShell";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Input } from "../components/ui/Input";
import alertService from "../utils/alert";
import { cn } from "../utils/cn";
import { formatCurrency } from "../utils/currency";
import { useCompanyCurrency } from "../hooks/useCompanySettings";
import PlanFeatureGroups from '../components/PlanFeatureGroups';
import { PageLoader } from '../components/crm/ui';
import CustomPlanBuilderModal from '../components/subscription/CustomPlanBuilderModal';

const loadRazorpaySDK = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(window.Razorpay);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
    document.head.appendChild(script);
  });

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const companyCurrency = useCompanyCurrency();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  const [couponInput, setCouponInput] = useState({});
  const [couponState, setCouponState] = useState({});
  const [cancelling, setCancelling] = useState(false);
  const [switching, setSwitching] = useState(null);
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);

  useEffect(() => {
    loadData();
    loadRazorpaySDK().catch(() => {});
  }, []);

  const handleCustomOrderCreated = (orderData) => {
    const { orderId, amount, currency, razorpayKeyId, prefill } = orderData;
    if (!window.Razorpay) {
      alertService.error("Error", "Payment gateway SDK failed to load. Please refresh.");
      return;
    }

    const options = {
      key: razorpayKeyId,
      amount: amount,
      currency: currency,
      name: "WaapLeads CRM",
      description: "Custom Tailored Subscription",
      order_id: orderId,
      prefill: {
        name: prefill?.name || "",
        email: prefill?.email || "",
        contact: prefill?.contact || "",
      },
      theme: { color: "#0176D3" },
      handler: async function (response) {
        try {
          await api.post("/subscriptions/verify-payment", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          navigate("/dashboard/subscription/payment-status");
        } catch (err) {
          alertService.error(
            "Verification Failed",
            err.response?.data?.message || "Payment verification failed. Please contact support if you were charged."
          );
        }
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [plansRes, subRes] = await Promise.all([
        api.get("/subscriptions/plans"),
        api.get("/subscriptions/current"),
      ]);
      setPlans(plansRes.data.plans || []);
      setSubscription(subRes.data.subscription);
    } catch (err) {
      alertService.error("Sync Error", "Could not retrieve subscription parameters.");
    } finally {
      setLoading(false);
    }
  };

  const applyCoupon = async (plan) => {
    const code = (couponInput[plan._id] || '').trim();
    if (!code) return;
    setCouponState(prev => ({ ...prev, [plan._id]: { loading: true } }));
    try {
      const { data } = await api.post('/coupons/validate', { code, planPrice: plan.price, planId: plan._id });
      setCouponState(prev => ({
        ...prev,
        [plan._id]: { discount: data.discount, finalPrice: data.finalPrice, code, message: data.message },
      }));
      alertService.toast('success', `Voucher applied: ${formatCurrency(data.discount, companyCurrency)} credit.`);
    } catch (err) {
      setCouponState(prev => ({
        ...prev,
        [plan._id]: { error: err.response?.data?.message || 'Invalid code' },
      }));
      alertService.error('Invalid Voucher', err.response?.data?.message || 'The specified code is not recognized.');
    }
  };

  const removeCoupon = (planId) => {
    setCouponInput(prev => ({ ...prev, [planId]: '' }));
    setCouponState(prev => ({ ...prev, [planId]: null }));
  };

  const handleSubscribe = async (plan) => {
    try {
      setPaying(plan._id);
      const appliedCoupon = couponState[plan._id];
      const { data } = await api.post("/subscriptions/create-order", {
        planId: plan._id,
        couponCode: appliedCoupon?.code || null,
      });
      const { orderId, amount, currency, razorpayKeyId, prefill } = data;

      if (!window.Razorpay) throw new Error("Payment gateway SDK initialization failure.");

      const options = {
        key: razorpayKeyId,
        amount: amount,
        currency: currency,
        name: "WaapLeads CRM",
        description: `Plan: ${plan.name}`,
        order_id: orderId,
        prefill: {
          name: prefill?.name || "",
          email: prefill?.email || "",
          contact: prefill?.contact || "",
        },
        theme: { color: "#0176D3" },
        handler: async function (response) {
          try {
            await api.post("/subscriptions/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              planId: plan._id,
            });
            navigate("/dashboard/subscription/payment-status");
          } catch (err) {
            alertService.error(
              "Verification Failed",
              err.response?.data?.message || "Payment could not be verified. Please contact support if you were charged."
            );
            setPaying(null);
          }
        },
        modal: { ondismiss: () => setPaying(null) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        alertService.error("Payment cancelled", response.error?.description || "Payment flow interrupted.");
        setPaying(null);
      });
      rzp.open();
    } catch (err) {
      alertService.error("Gateway Sync Error", err.response?.data?.error || err.message || "Failed to initialize secure transaction.");
      setPaying(null);
    }
  };

  const handleCancel = async () => {
    const confirmed = await alertService.confirm(
      "Cancel Subscription",
      `You'll keep access until ${subscription?.endDate ? new Date(subscription.endDate).toLocaleDateString() : "the end of your billing period"}. Continue?`
    );
    if (!confirmed) return;
    try {
      setCancelling(true);
      const { data } = await api.post("/subscriptions/cancel");
      alertService.toast("success", data.message || "Subscription set to cancel at period end.");
      await loadData();
    } catch (err) {
      alertService.error("Cancellation Failed", err.response?.data?.error || "Could not cancel subscription.");
    } finally {
      setCancelling(false);
    }
  };

  const handleResume = async () => {
    try {
      setCancelling(true);
      await api.post("/subscriptions/resume");
      alertService.toast("success", "Subscription will now renew as normal.");
      await loadData();
    } catch (err) {
      alertService.error("Failed", err.response?.data?.error || "Could not resume subscription.");
    } finally {
      setCancelling(false);
    }
  };

  const handleDowngrade = async (plan) => {
    const confirmed = await alertService.confirm(
      "Switch Plan",
      `Switch to the ${plan.name} plan now? This takes effect immediately, no new payment required.`
    );
    if (!confirmed) return;
    try {
      setSwitching(plan._id);
      const { data } = await api.post("/subscriptions/downgrade", { planId: plan._id });
      alertService.toast("success", data.message || "Plan switched.");
      await loadData();
    } catch (err) {
      alertService.error("Switch Failed", err.response?.data?.error || err.response?.data?.message || "Could not switch plan.");
    } finally {
      setSwitching(null);
    }
  };

  const currentPlanId = subscription?.plan?._id;
  const isExpired = subscription && !subscription.isActive;

  if (loading) {
    return (
      <PageLoader />
    );
  }

  const dashboardStats = [
    { label: 'Active Tier', value: subscription?.plan?.name || 'Limited', icon: Package },
    { label: 'Network Multiplier', value: plans.length, icon: Cpu },
    { label: 'Uptime', value: '99.9%', icon: ShieldCheck },
    { label: 'Billing Index', value: subscription?.isActive ? 'Active' : 'N/A', icon: RefreshCw },
  ];

  return (
    <DashboardModuleShell
      title="Subscription Management"
      subtitle="Optimize your workspace capacity, manage billing cycles, and scale your deployment strategy."
      icon={CreditCard}
      stats={dashboardStats}
    >
      <div className="space-y-12 pb-12">
        {/* Current Active Plan Card */}
        <section className="animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="px-4 py-3 mb-4 text-[11px] font-bold text-[#706E6B] uppercase border-b border-[#F3F2F2]">
            Subscription Overview
          </div>
          <Card className={cn(
 "p-0 overflow-hidden relative border-[#E5E5E5] shadow-sm",
 isExpired ? "bg-[#FEF1EE]/30" : "bg-[#EBF7E6]/30"
 )}>
            <div className={cn(
 "absolute top-0 left-0 w-1.5 h-full",
 isExpired ? "bg-[#BA0517]" : "bg-[#2E844A]"
 )} />
            <div className="p-8 lg:p-12 flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              <div className={cn(
 "w-24 h-24 rounded-lg flex items-center justify-center border shadow-sm",
 isExpired ? "bg-white text-[#BA0517] border-[#FDDDE3]" : "bg-white text-[#2E844A] border-[#CDEFC4]"
 )}>
                {subscription?.isActive ? <ShieldCheck size={48} strokeWidth={1.5} /> : <AlertCircle size={48} strokeWidth={1.5} />}
              </div>
              
              <div className="flex-1 text-center lg:text-left space-y-4">
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <h3 className="text-3xl font-bold text-[#080707] tracking-tight">
                    {subscription?.plan?.name || "No Production Plan"}
                  </h3>
                  <Badge variant={isExpired ? "rose" : "emerald"}>
                    {isExpired ? "Suspended" : "Active"}
                  </Badge>
                </div>
                
                {subscription?.endDate && (
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-8">
                    <div className="flex items-center gap-2 text-[12px] font-bold text-[#706E6B]">
                      <Calendar size={14} className="text-[#0176D3]" />
                      {isExpired ? "Terminated:" : subscription.cancelAtPeriodEnd ? "Cancels:" : "Next Cycle:"}
                      <span className="text-[#080707] ml-1">{new Date(subscription.endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                    {!isExpired && (
                      <div className="flex items-center gap-2 text-[12px] font-bold text-[#706E6B]">
                        <Clock size={14} className="text-[#DD7A01]" />
                        <span className="text-[#080707]">{subscription.daysRemaining} days {subscription.cancelAtPeriodEnd ? "remaining" : "until renewal"}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-[12px] font-bold text-[#706E6B]">
                      <RefreshCw size={14} className="text-[#0176D3]" />
                      Cycle: <span className="text-[#080707]">{subscription.billingCycle?.toUpperCase() || 'MONTHLY'}</span>
                    </div>
                    {subscription.cancelAtPeriodEnd && !isExpired && (
                      <Badge variant="amber">Cancellation Scheduled</Badge>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                {!isExpired && subscription?.plan && (
                  subscription.cancelAtPeriodEnd ? (
                    <Button
                      variant="outline"
                      className="h-12 px-8 font-bold text-[12px] uppercase tracking-wide"
                      loading={cancelling}
                      onClick={handleResume}
                    >
                      Resume Subscription
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="h-12 px-8 font-bold text-[12px] uppercase tracking-wide text-[#BA0517] border-[#FDDDE3] hover:bg-[#FEF1EE]"
                      loading={cancelling}
                      onClick={handleCancel}
                    >
                      Cancel Subscription
                    </Button>
                  )
                )}
                <Button
                  variant="outline"
                  className="h-12 px-8 font-bold text-[12px] uppercase tracking-wide group"
                  onClick={() => navigate('/dashboard/settings')}
                >
                  Resource Usage
                  <ChevronRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            </div>
          </Card>
        </section>

        {/* Custom Plan Builder Banner */}
        <div className="bg-gradient-to-r from-[#0176D3]/10 via-[#0176D3]/5 to-transparent border border-[#0176D3]/20 rounded-xl p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-[#0176D3] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                Self-Serve Customization
              </span>
              <h3 className="text-base font-bold text-[#181818]">Need a Tailored Plan?</h3>
            </div>
            <p className="text-xs text-[#5C5C5C] max-w-xl">
              Pick only the CRM modules you need (Sales, Marketing, Finance, Meta Ads, AI Suite) and configure exact WhatsApp devices, seat quotas, and message limits.
            </p>
          </div>
          <Button
            onClick={() => setIsCustomModalOpen(true)}
            className="bg-[#0176D3] text-white hover:bg-[#015FB0] text-xs font-bold px-5 py-2.5 rounded-lg shrink-0 shadow-sm flex items-center gap-2"
          >
            <span>Design Custom Plan</span>
            <ChevronRight size={15} />
          </Button>
        </div>

        {/* Pricing Matrix */}
        <section className="animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="px-4 py-3 mb-6 text-[11px] font-bold text-[#706E6B] uppercase border-b border-[#F3F2F2]">
            Standard Infrastructure Tiers
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans.map((plan) => {
              const isCurrent = currentPlanId === plan._id && subscription?.isActive;
              const isLoading = paying === plan._id;
              const isPopular = plan.price > 1000;
              const appliedCoupon = couponState[plan._id];

              return (
                <Card
                  key={plan._id}
                  className={cn(
 "flex flex-col group relative overflow-hidden transition-all duration-300 border-[#E5E5E5] shadow-sm hover:shadow-sm hover:border-[#0176D3]/30",
 isCurrent && "border-[#0176D3] ring-4 ring-[#0176D3]/5"
 )}
                >
                  {isPopular && !isCurrent && (
                    <div className="absolute top-0 right-0 bg-[#0176D3] text-white text-[10px] font-bold px-4 py-1.5 rounded-bl uppercase z-10">
                      Recommended
                    </div>
                  )}

                  <div className="p-8 space-y-8 flex-1">
                    <div className="space-y-1">
                      <h2 className="text-2xl font-bold text-[#080707] group-hover:text-[#0176D3] transition-colors">{plan.name}</h2>
                      <p className="text-[12px] text-[#706E6B] font-medium uppercase ">{plan.description || "Standard plan"}</p>
                    </div>

                    <div className="flex items-baseline gap-1">
                      <span className="text-5xl font-bold text-[#080707] tracking-tight">
                        {formatCurrency(appliedCoupon?.finalPrice != null ? appliedCoupon.finalPrice : plan.price, companyCurrency)}
                      </span>
                      <span className="text-[13px] font-bold text-[#706E6B] ml-1">/{plan.billingCycle || "MO"}</span>
                      {appliedCoupon?.discount > 0 && (
                        <span className="text-lg font-bold text-[#C9C9C9] line-through ml-4 italic">{formatCurrency(plan.price, companyCurrency)}</span>
                      )}
                    </div>

                    {/* Voucher Application Layer */}
                    {!isCurrent && (
                      <div className="space-y-4 pt-6 border-t border-[#F3F2F2]">
                        {appliedCoupon?.discount > 0 ? (
                          <div className="px-4 py-3 rounded bg-[#EBF7E6] border border-[#CDEFC4] flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Tag size={16} className="text-[#2E844A]" />
                              <div className="text-[11px] font-bold text-[#2E844A] uppercase ">
                                Saved {formatCurrency(appliedCoupon.discount, companyCurrency)}
                              </div>
                            </div>
                            <button className="text-[#2E844A] hover:text-[#BA0517] transition-colors" onClick={() => removeCoupon(plan._id)}>
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <input
                              className="flex-1 h-9 px-4 text-[12px] bg-[#F7F9FB] border border-[#D8DDE6] rounded outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
                              placeholder="VOUCHER"
                              value={couponInput[plan._id] || ''}
                              onChange={e => setCouponInput(prev => ({ ...prev, [plan._id]: e.target.value.toUpperCase() }))}
                              disabled={!!paying}
                            />
                            <Button 
                              variant="outline" 
                              className="h-10 px-4 text-[11px] font-bold uppercase" 
                              onClick={() => applyCoupon(plan)}
                              disabled={!couponInput[plan._id]?.trim() || !!paying}
                              loading={appliedCoupon?.loading}
                            >
                              Apply
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Feature Matrix */}
                    <ul className="space-y-4 pt-4">
                      {[
                        { label: `${plan.maxDevices} Managed Nodes`, icon: Smartphone },
                        { label: `${plan.monthlyMessageLimit.toLocaleString()} Messages`, icon: Activity },
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-center gap-3 group/item">
                          <Check size={16} className="text-[#2E844A] shrink-0" strokeWidth={3} />
                          <span className="text-[13px] text-[#5C5C5C] font-medium">{item.label}</span>
                        </li>
                      ))}
                    </ul>
                    <PlanFeatureGroups groups={plan.featureGroups || []} />
                  </div>

                  <div className="p-6 bg-[#F7F9FB] border-t border-[#F3F2F2]">
                    {(() => {
                      const isDowngrade = !isCurrent && subscription?.isActive && subscription?.plan?.price != null && plan.price <= subscription.plan.price;
                      const isSwitching = switching === plan._id;
                      return (
                        <Button
                          variant={isCurrent ? "outline" : "brand"}
                          className="w-full h-12 text-sm font-bold shadow-sm"
                          disabled={isCurrent || isLoading || isSwitching || !!paying || !!switching}
                          loading={isLoading || isSwitching}
                          icon={isCurrent ? ShieldCheck : Zap}
                          onClick={() => {
                            if (isCurrent) return;
                            if (isDowngrade) return handleDowngrade(plan);
                            return handleSubscribe(plan);
                          }}
                        >
                          {isCurrent ? "Current Plan" : isDowngrade ? "Switch to This Plan" : "Choose Plan"}
                        </Button>
                      );
                    })()}
                  </div>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Global Usage Telemetry */}
        {subscription?.usageStats && (
          <section className="animate-in fade-in slide-in-from-bottom-4 duration-1000">
            <div className="px-4 py-3 mb-6 text-[11px] font-bold text-[#706E6B] uppercase border-b border-[#F3F2F2]">
              Workspace Telemetry
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { label: 'Transmission Volume', val: subscription.usageStats.messagesThisMonth?.toLocaleString(), icon: Activity, color: 'text-[#0176D3]' },
                { label: 'Total Messages Sent', val: subscription.usageStats.totalMessagesAllTime?.toLocaleString(), icon: Globe, color: 'text-[#0176D3]' },
                { label: 'Runtime Stability', val: '99.99%', icon: ShieldCheck, color: 'text-[#2E844A]' }
              ].map((s, i) => (
                <Card key={i} className="p-8 border-[#E5E5E5] shadow-sm flex items-center gap-6 group hover:border-[#0176D3]/20 transition-all">
                  <div className={cn(
 "w-14 h-14 rounded-lg bg-[#F7F9FB] border border-[#F3F2F2] flex items-center justify-center transition-all group-hover:scale-110",
 s.color
 )}>
                    <s.icon size={28} strokeWidth={1.5} />
                  </div>
                  <div className="space-y-0.5">
                    <div className="text-[11px] font-bold text-[#706E6B] uppercase ">{s.label}</div>
                    <div className="text-3xl font-bold text-[#080707] tracking-tight">{s.val || 0}</div>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>

      <CustomPlanBuilderModal
        isOpen={isCustomModalOpen}
        onClose={() => setIsCustomModalOpen(false)}
        onOrderCreated={handleCustomOrderCreated}
      />
    </DashboardModuleShell>
  );
}
